import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { statisticsAPI } from '../services/api';
import '../styles/Statistics.css';

const Statistics = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        try {
            const response = await statisticsAPI.getDashboard();
            setStats(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error loading statistics:', error);
            setLoading(false);
        }
    };

    const formatTime = (minutes) => {
        if (minutes < 60) {
            return `${minutes} min`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    if (loading) {
        return (
            <>
                <Sidebar />
                <div className="main-content">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <div className="main-content">
                <div className="statistics-container">
                <h1>📊 Statistics & Analytics</h1>

                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">✅</div>
                        <div className="stat-content">
                            <h3>Tasks Completed</h3>
                            <div className="stat-value">{stats.totalTasksCompleted}</div>
                            <div className="stat-subtitle">All time</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">⏱️</div>
                        <div className="stat-content">
                            <h3>Total Time Spent</h3>
                            <div className="stat-value">{formatTime(stats.totalTimeSpent)}</div>
                            <div className="stat-subtitle">Across all tasks</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">📈</div>
                        <div className="stat-content">
                            <h3>Average Time/Task</h3>
                            <div className="stat-value">{formatTime(stats.averageTimePerTask)}</div>
                            <div className="stat-subtitle">Per completed task</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">🎯</div>
                        <div className="stat-content">
                            <h3>Today's Completed</h3>
                            <div className="stat-value">{stats.tasksCompletedToday}</div>
                            <div className="stat-subtitle">Keep going!</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">📝</div>
                        <div className="stat-content">
                            <h3>Active Tasks</h3>
                            <div className="stat-value">{stats.activeTasks}</div>
                            <div className="stat-subtitle">Pending tasks</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">⚠️</div>
                        <div className="stat-content">
                            <h3>Exceeded Duration</h3>
                            <div className="stat-value">{stats.tasksExceededDuration}</div>
                            <div className="stat-subtitle">{stats.exceededPercentage}% of total</div>
                        </div>
                    </div>
                </div>

                <div className="charts-section">
                    <div className="chart-card">
                        <h2>Tasks by Group</h2>
                        {stats.tasksByGroup && stats.tasksByGroup.length > 0 ? (
                            <div className="bar-chart">
                                {stats.tasksByGroup.map((group, index) => (
                                    <div key={index} className="bar-item">
                                        <div className="bar-label">
                                            <span 
                                                className="bar-color" 
                                                style={{ backgroundColor: group.color || '#ccc' }}
                                            ></span>
                                            {group.name || 'No Group'}
                                        </div>
                                        <div className="bar-container">
                                            <div 
                                                className="bar-fill" 
                                                style={{ 
                                                    width: `${(group.count / stats.totalTasksCompleted) * 100}%`,
                                                    backgroundColor: group.color || '#ccc'
                                                }}
                                            ></div>
                                        </div>
                                        <div className="bar-value">{group.count}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-chart">
                                <p>No data available yet</p>
                            </div>
                        )}
                    </div>

                    <div className="chart-card">
                        <h2>Completion Trend (Last 7 Days)</h2>
                        {stats.completionTrend && stats.completionTrend.length > 0 ? (
                            <div className="line-chart">
                                {stats.completionTrend.map((day, index) => (
                                    <div key={index} className="trend-item">
                                        <div className="trend-date">
                                            {new Date(day.date).toLocaleDateString('en-US', { 
                                                month: 'short', 
                                                day: 'numeric' 
                                            })}
                                        </div>
                                        <div className="trend-bar-container">
                                            <div 
                                                className="trend-bar" 
                                                style={{ 
                                                    height: `${(day.count / Math.max(...stats.completionTrend.map(d => d.count))) * 100}%` 
                                                }}
                                            ></div>
                                        </div>
                                        <div className="trend-count">{day.count}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-chart">
                                <p>No completion data for the last 7 days</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="performance-summary">
                    <h2>Performance Summary</h2>
                    <div className="summary-grid">
                        <div className="summary-item">
                            <div className="summary-label">On-Time Completion Rate</div>
                            <div className="summary-value success">
                                {100 - stats.exceededPercentage}%
                            </div>
                        </div>
                        <div className="summary-item">
                            <div className="summary-label">Tasks Over Expected Time</div>
                            <div className="summary-value warning">
                                {stats.exceededPercentage}%
                            </div>
                        </div>
                        <div className="summary-item">
                            <div className="summary-label">Productivity Score</div>
                            <div className="summary-value info">
                                {stats.totalTasksCompleted > 0 
                                    ? Math.min(100, Math.round((stats.tasksCompletedToday / stats.totalTasksCompleted) * 1000))
                                    : 0}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Statistics;
